import type { PiniaPlugin } from 'pinia';
import { isProdEnvironment } from '@/settings/environment.utils.js';

// Accessing via module reference (not destructured) so reads are live — the JS module
// exports `let sessionTracker` which is reassigned after each initializeSessionTracker call.
// Destructuring would capture undefined at load time and break the dispose guard.
// eslint-disable-next-line @typescript-eslint/no-require-imports
const sessionActivityUtil = require('@utils/session/session-activity.util') as {
    sessionTracker: { dispose: () => void; inactivityThreshold: number } | null | undefined;
    initializeSessionTracker: (config: { inactivityThreshold: number }) => void;
};

export const sessionPlugin: PiniaPlugin = ({ store }) => {
    if (window.Cypress) return;

    if (store.$id === 'subscription') {
        store.$onAction(({ name, after }) => {
            if (name !== 'setActive' && name !== 'setActiveById') return;

            after(() => {
                const inactivityThreshold = Number(store.active.maxSessionInactivity);
                if (sessionActivityUtil.sessionTracker?.inactivityThreshold !== inactivityThreshold) {
                    setupTracker(inactivityThreshold);
                }
            });
        });
    }

    if (store.$id === 'auth') {
        store.$onAction(({ name, args, after }) => {
            if (name === 'logout') {
                disposeTracker();

                return;
            }
            if (name !== 'setAuthenticated' || (args[0] as { authenticated?: boolean })?.authenticated) return;
            after(disposeTracker);
        });
    }
};

export function setupTracker (inactivityThreshold: number): void {
    if (window.Cypress) return;

    if (sessionActivityUtil.sessionTracker) {
        sessionActivityUtil.sessionTracker.dispose();
    }

    sessionActivityUtil.initializeSessionTracker({ inactivityThreshold });
}

function disposeTracker (): void {
    if (!sessionActivityUtil.sessionTracker) return;

    try {
        sessionActivityUtil.sessionTracker.dispose();
    } catch (error) {
        if (!isProdEnvironment()) {
            // eslint-disable-next-line no-console
            console.warn('Issue setting up session activity with error', error);
        }
    }
}
